import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { Close, PlayArrow, Schedule, LocalFireDepartment, FormatListNumbered, CheckCircle } from '@mui/icons-material';
import StopMotionImage from 'components/play/StopMotionImage';
import PlayExerciseDetailModal from 'components/play/PlayExerciseDetailModal';
import { dayLabel, muscleLabel, primaryMuscleOf } from 'components/play/constants';
import { estimateWorkout } from 'components/play/workoutEstimate';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

// Stat compacto de la cabecera (duración / series / calorías).
function Stat({ icon: Icon, label }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
      <Icon sx={{ fontSize: 15, color: 'gold.main' }} />
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </Typography>
    </Stack>
  );
}

Stat.propTypes = { icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired };

/**
 * OTRIX Play — detalle de la rutina de UN día (se abre al tocar su card en la Agenda).
 *
 * Muestra qué toca ese día (ejercicios, series y reps) y deja ver la técnica de cada ejercicio
 * antes de arrancar, para no tener que entrar al entrenamiento a ciegas.
 */
function PlayRoutineDayModal({
  open,
  dayOfWeek = null,
  items = [],
  canStart = true,
  isPast = false,
  workout = null,
  onClose,
  onStart
}) {
  const { t } = useTranslation(['play']);
  const [detailTarget, setDetailTarget] = useState(null);

  const muscle = primaryMuscleOf(items);
  const estimate = estimateWorkout(items);
  const workoutOk = workout?.status === 'completed';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'surface.content',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'hairline.main'
          }
        }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {dayOfWeek ? dayLabel(t, dayOfWeek) : ''}
            </Typography>
            <Typography variant="h6" fontWeight={800} noWrap>
              {muscle ? t('todayCard.titleMuscle', { ns: 'play', muscle: muscleLabel(t, muscle) }) : t('todayCard.titleGeneric', { ns: 'play' })}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 0.75, flexWrap: 'wrap' }} useFlexGap>
              <Stat icon={FormatListNumbered} label={t('todayCard.exercisesToday', { ns: 'play', count: estimate.exerciseCount, sets: estimate.totalSets })} />
              <Stat icon={Schedule} label={t('todayCard.minutes', { ns: 'play', count: estimate.durationMin })} />
              <Stat icon={LocalFireDepartment} label={t('todayCard.kcal', { ns: 'play', count: estimate.calories })} />
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary', flex: 'none' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <DialogContent>
          {/* Entrenamiento registrado ese día: el logro va primero (verde o ámbar). */}
          {workout && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: '12px',
                backgroundColor: workoutOk ? 'sessionStatus.completed.bg' : 'sessionStatus.pending.bg',
                color: workoutOk ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg'
              }}>
              <CheckCircle sx={{ fontSize: 18, flex: 'none' }} />
              <Typography variant="caption" fontWeight={800} sx={{ flex: 1, minWidth: 0 }}>
                {workoutOk
                  ? t('agenda.dayCompleted', { ns: 'play', defaultValue: 'Entrenamiento completado este día' })
                  : t('agenda.dayPartial', {
                      ns: 'play',
                      percent: workout.completion_percent || 0,
                      defaultValue: 'Entrenaste este día ({{percent}}% de las series)'
                    })}
              </Typography>
              {workout.estimated_calories > 0 && (
                <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                  {t('todayCard.kcal', { ns: 'play', count: workout.estimated_calories })}
                </Typography>
              )}
            </Stack>
          )}

          <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item, idx) => (
              <Stack
                key={item.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 1.25,
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: 'hairline.main',
                  backgroundColor: 'surface.raised'
                }}>
                <Typography
                  variant="caption"
                  fontWeight={800}
                  sx={{ width: 16, flex: 'none', textAlign: 'center', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {idx + 1}
                </Typography>

                <Box sx={{ width: 60, flex: 'none', borderRadius: '10px', overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main' }}>
                  <StopMotionImage frame1={item.exercise?.image_url_1} frame2={item.exercise?.image_url_2} alt={item.exercise?.localized_name} height={68} />
                </Box>

                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={800} noWrap>
                    {item.exercise?.localized_name}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    {item.exercise?.muscle_group && (
                      <Chip
                        size="small"
                        label={muscleLabel(t, item.exercise.muscle_group)}
                        sx={{ height: 20, fontSize: '0.66rem', bgcolor: 'gold.soft', color: 'gold.main' }}
                      />
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {[
                        item.target_sets ? `${item.target_sets} ${t('card.series', { ns: 'play' })}` : null,
                        item.target_reps ? `${item.target_reps} ${t('card.reps', { ns: 'play' })}` : null
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<PlayArrow sx={{ fontSize: 15 }} />}
                    onClick={() => setDetailTarget(item.exercise)}
                    sx={{ alignSelf: 'flex-start', ml: -0.75, color: 'gold.main', textTransform: 'none', fontWeight: 700, minHeight: 0, py: 0.25 }}>
                    {t('card.viewTechnique', { ns: 'play' })}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>

          <Button fullWidth disabled={!canStart} startIcon={<PlayArrow />} onClick={onStart} sx={{ ...premiumButtonSx, mt: 2.5 }}>
            {t('routine.startRoutine', { ns: 'play' })}
          </Button>
          {!canStart && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}>
              {isPast
                ? t('agenda.pastDay', { ns: 'play' })
                : t('agenda.futureDay', {
                    ns: 'play',
                    defaultValue: 'Este día aún no llega: puedes revisarlo, pero solo se inicia el día de hoy.'
                  })}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Técnica del ejercicio (stop-motion + pasos). Solo lectura: ya está en la rutina. */}
      <PlayExerciseDetailModal exercise={detailTarget} open={Boolean(detailTarget)} onClose={() => setDetailTarget(null)} alreadyAdded />
    </>
  );
}

PlayRoutineDayModal.propTypes = {
  open: PropTypes.bool.isRequired,
  dayOfWeek: PropTypes.number,
  /** WeeklyRoutineItem[] del día (con `exercise` completo). */
  items: PropTypes.arrayOf(PropTypes.object),
  /** true SOLO el día de HOY: pasados y futuros se consultan pero no se arrancan. */
  canStart: PropTypes.bool,
  /** Distingue el copy del aviso: día ya pasado vs día que aún no llega. */
  isPast: PropTypes.bool,
  /** PlayWorkoutSession registrada ese día (status, % y kcal), si existe. */
  workout: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired
};

export default PlayRoutineDayModal;
